/* Karchel — product page behaviour.
 *
 *   1. Detail tabs      — WAI-ARIA tabs with automatic activation.
 *   2. Read more        — clamps the description panel and reveals the rest.
 *   3. Sticky gallery   — keeps the image column in view while it is pinned.
 *   4. Scroll reveal    — gives each custom section a quiet entrance.
 *
 * All four are progressive enhancements. With JS off the tablist stays
 * hidden, every panel renders open and unclamped, the gallery falls back
 * to the theme's own top-anchored sticky, and nothing is ever hidden by
 * the reveal layer.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ tabs */

  function initTabs(root) {
    if (root.dataset.kptReady === 'true') return;

    var tabs = Array.prototype.slice.call(root.querySelectorAll('.kpt__tab'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('.kpt__panel'));
    if (tabs.length === 0 || tabs.length !== panels.length) return;

    root.dataset.kptReady = 'true';
    root.classList.add('kpt--ready');

    function select(index, moveFocus) {
      for (var i = 0; i < tabs.length; i++) {
        var active = i === index;
        tabs[i].setAttribute('aria-selected', active ? 'true' : 'false');
        tabs[i].setAttribute('tabindex', active ? '0' : '-1');
        panels[i].hidden = !active;
      }
      if (moveFocus) tabs[index].focus();
      measureClamps(panels[index]);
    }

    select(0, false);

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        select(index, false);
      });

      tab.addEventListener('keydown', function (event) {
        var last = tabs.length - 1;
        var next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
            next = index === 0 ? last : index - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }

        event.preventDefault();
        select(next, true);
      });
    });
  }

  /* -------------------------------------------------------------- read more */

  function clampBody(clamp) {
    return clamp.querySelector('.kpt__body');
  }

  function measureClamp(clamp) {
    var body = clampBody(clamp);
    var button = clamp.querySelector('.kpt__more');
    if (!body || !button) return;

    if (clamp.dataset.collapsed === 'false' && clamp.dataset.kptUserExpanded === 'true') return;

    var limit = parseInt(getComputedStyle(clamp).getPropertyValue('--kpt-clamp-h'), 10) || 560;

    var previous = clamp.dataset.collapsed;
    clamp.dataset.collapsed = 'false';
    var natural = body.scrollHeight;
    clamp.dataset.collapsed = previous;

    if (natural === 0) return;

    if (natural <= limit + 80) {
      clamp.dataset.collapsed = 'false';
      clamp.dataset.kptNeeded = 'false';
      button.hidden = true;
    } else {
      clamp.dataset.kptNeeded = 'true';
      button.hidden = false;
      if (clamp.dataset.kptUserExpanded !== 'true') clamp.dataset.collapsed = 'true';
    }
  }

  function measureClamps(scope) {
    var context = scope || document;
    var clamps = context.querySelectorAll('[data-kpt-clamp]');
    for (var i = 0; i < clamps.length; i++) measureClamp(clamps[i]);
  }

  function initClamp(clamp) {
    if (clamp.dataset.kptClampReady === 'true') return;
    clamp.dataset.kptClampReady = 'true';

    var button = clamp.querySelector('.kpt__more');
    var label = clamp.querySelector('.kpt__more-label');
    if (!button) return;

    var more = button.getAttribute('data-more') || (label ? label.textContent : 'Read more');
    var less = button.getAttribute('data-less') || 'Show less';

    button.addEventListener('click', function () {
      var collapsed = clamp.dataset.collapsed !== 'false';
      clamp.dataset.collapsed = collapsed ? 'false' : 'true';
      clamp.dataset.kptUserExpanded = collapsed ? 'true' : 'false';
      button.setAttribute('aria-expanded', String(collapsed));
      if (label) label.textContent = collapsed ? less : more;

      if (!collapsed) {
        var top = clamp.getBoundingClientRect().top;
        if (top < 0) clamp.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    });

    measureClamp(clamp);
  }

  function initClamps(scope) {
    var context = scope || document;
    var clamps = context.querySelectorAll('[data-kpt-clamp]');
    for (var i = 0; i < clamps.length; i++) initClamp(clamps[i]);
  }

  /* ------------------------------------------------------- sticky gallery
   *
   * Karchel pins the GALLERY, not the buy box — the image stays in view
   * while the info column scrolls normally beside it. The theme's own
   * base CSS is overridden in karchel-product.css to make .product-info
   * static and .product-gallery sticky instead.
   *
   * A gallery taller than the viewport would otherwise pin at the top and
   * run its lower images past the bottom of the fold, permanently out of
   * view. When that happens, anchor its BOTTOM instead so the whole image
   * stack cycles into view as the reader scrolls.
   */

  var BOTTOM_GAP = 24;
  var stickyGallery = null;

  function stickyOffset(el) {
    if (getComputedStyle(el).position !== 'sticky') return null;

    var height = el.getBoundingClientRect().height;
    var viewport = window.innerHeight;
    if (!height || !viewport) return null;

    var header = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')
    ) || 0;
    var natural = header + 20;

    if (height + natural + BOTTOM_GAP <= viewport) return null;

    return Math.round(viewport - height - BOTTOM_GAP);
  }

  function syncSticky() {
    if (!stickyGallery) return;
    var offset = stickyOffset(stickyGallery);
    if (offset === null) {
      stickyGallery.style.removeProperty('--kp-sticky-top');
    } else {
      stickyGallery.style.setProperty('--kp-sticky-top', offset + 'px');
    }
  }

  function initSticky() {
    var gallery = document.querySelector('.product > .product-gallery');
    if (!gallery || gallery.dataset.kpStickyReady === 'true') return;
    gallery.dataset.kpStickyReady = 'true';
    stickyGallery = gallery;

    syncSticky();

    if (typeof ResizeObserver === 'function') {
      var pending = null;
      new ResizeObserver(function () {
        if (pending) return;
        pending = setTimeout(function () {
          pending = null;
          syncSticky();
        }, 0);
      }).observe(gallery);
    }

    window.addEventListener('resize', syncSticky);
  }

  /* ----------------------------------------------------------- scroll reveal
   *
   * Each custom PDP section fades and rises a short distance as it comes
   * into view, and repeated items inside it (use-case cards, badges, stat
   * figures, FAQ rows, comparison rows) follow one beat behind each other.
   * The travel is short and the curve decelerates hard, so the page feels
   * composed as you move down it rather than animated at you.
   *
   * FAILING SAFE IS THE WHOLE DESIGN HERE. Content a reader cannot see is a
   * far worse outcome than an entrance that does not play, so there are
   * three independent ways for a section to end up visible and only one
   * way for it to be hidden:
   *
   *   1. Armed only when it is genuinely below the fold at init.
   *   2. IntersectionObserver reveals it on approach (the normal path).
   *   3. A passive scroll/resize sweep reveals anything that has reached
   *      the fold, whatever the observer did — and a delayed sweep after
   *      load catches a viewport that was not measurable at init.
   *
   * Nothing is armed at all unless IntersectionObserver exists AND the
   * viewport reports a usable height. A zero-height or unmeasurable
   * viewport (hidden frame, headless capture, backgrounded tab restore)
   * would otherwise arm every section and then never fire the observer,
   * stranding the page blank — which is exactly what happens without this
   * guard. CSS hides nothing on its own: the hiding rules are gated behind
   * .kp-motion on <html>, which only this function adds.
   */

  var REVEAL_SECTIONS = '.kp > .kp-sec, .kp > .kuse, .kp > .kbadge';

  /* Containers whose direct children stagger, in reading order. Anything
     not listed simply reveals with its section. */
  var STAGGER_CONTAINERS = [
    '.kuse-grid',
    '.kbadge-row',
    '.kp-stats',
    '.kp-faq',
    '.kp-vs-scroll table tbody'
  ].join(',');

  var revealObserver = null;
  var sweepQueued = false;
  var sweepBound = false;

  function revealNow(el) {
    el.setAttribute('data-kp-reveal', 'in');
    var kids = el.querySelectorAll('[data-kp-reveal-child]');
    for (var i = 0; i < kids.length; i++) kids[i].setAttribute('data-kp-reveal-child', 'in');
  }

  function markChildren(section) {
    var containers = section.querySelectorAll(STAGGER_CONTAINERS);

    for (var c = 0; c < containers.length; c++) {
      var kids = containers[c].children;
      var index = 0;

      for (var k = 0; k < kids.length; k++) {
        var kid = kids[k];
        if (kid.nodeType !== 1) continue;
        kid.setAttribute('data-kp-reveal-child', 'pending');
        kid.style.setProperty('--kp-i', String(index));
        index++;
        if (index > 11) index = 11; /* cap the tail so a long FAQ never crawls */
      }
    }
  }

  /* Backstop: reveal anything still pending that has reached the fold,
     regardless of what the observer did or did not do. */
  function sweep() {
    sweepQueued = false;

    var viewport = window.innerHeight || 0;
    if (viewport < 1) return;

    var pending = document.querySelectorAll('[data-kp-reveal="pending"]');
    for (var i = 0; i < pending.length; i++) {
      if (pending[i].getBoundingClientRect().top < viewport * 0.98) {
        revealNow(pending[i]);
        if (revealObserver) revealObserver.unobserve(pending[i]);
      }
    }
  }

  function queueSweep() {
    if (sweepQueued) return;
    sweepQueued = true;
    requestAnimationFrame(sweep);
  }

  function bindSweep() {
    if (sweepBound) return;
    sweepBound = true;
    window.addEventListener('scroll', queueSweep, { passive: true });
    window.addEventListener('resize', queueSweep, { passive: true });
  }

  function getObserver() {
    if (revealObserver) return revealObserver;

    revealObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          revealNow(entries[i].target);
          revealObserver.unobserve(entries[i].target);
        }
      },
      {
        /* Fires slightly before the section's top edge clears the bottom of
           the viewport, so the entrance is already underway by the time it
           is properly on screen rather than starting late and being seen. */
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.01
      }
    );

    return revealObserver;
  }

  function initReveal(scope) {
    var context = scope || document;
    var sections = context.querySelectorAll(REVEAL_SECTIONS);
    if (sections.length === 0) return;

    var viewport = window.innerHeight || 0;

    /* Cannot observe, or cannot trust the measurement: leave the page
       entirely alone rather than arm something that may never fire. */
    if (typeof IntersectionObserver !== 'function' || viewport < 1) return;

    document.documentElement.classList.add('kp-motion');
    bindSweep();

    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (section.dataset.kpRevealReady === 'true') continue;
      section.dataset.kpRevealReady = 'true';

      /* Already on screen, or close enough that the reader is about to be
         looking at it: show it outright. Hiding it now would be a flash. */
      if (section.getBoundingClientRect().top < viewport * 0.92) {
        revealNow(section);
        continue;
      }

      section.setAttribute('data-kp-reveal', 'pending');
      markChildren(section);
      getObserver().observe(section);
    }
  }

  /* -------------------------------------------------------------- bootstrap */

  function initAll(scope) {
    var context = scope || document;
    var roots = context.querySelectorAll('.kpt');
    for (var i = 0; i < roots.length; i++) initTabs(roots[i]);
    initClamps(context);
    initSticky();
    initReveal(context);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  window.addEventListener('load', function () {
    measureClamps();
    syncSticky();
    /* The viewport may only have become measurable now (fonts, images, a
       tab restored from the background). Arm whatever was skipped, then
       sweep so nothing that is already on screen stays hidden. */
    initReveal();
    sweep();
    /* One last sweep after layout has certainly settled. Cheap, and it is
       the difference between a stuck section and a visible one. */
    setTimeout(sweep, 2000);
  });

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      measureClamps();
    }, 200);
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (stickyGallery) {
      delete stickyGallery.dataset.kpStickyReady;
      stickyGallery = null;
    }
    initAll(event.target);
  });
})();
